import AccessTime from '@mui/icons-material/AccessTime'
import { Button, DialogActions, DialogContent, Box, Avatar, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getDataMapFromCommonResponse,
  getMessageMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
  getImageLink,
} from 'src/mentor/helpers/utilityFunctions'
import { putSPDataWithoutParams } from 'src/supportPortal/services/services'

export default function RosterItemViewer({
  selectedSlot,
  selectedItem,
  legendItems,
  getSidebarLegendValue,
  getSidebarLegendLabel,
  getSidebarItemReferenceFromRosterItem,
  removeSlotFromRosterList,
  closePopup,
  sidebarIcon,
  disableSlotApiSignature = '/api/secure/cc/support/roster/disable',
}) {
  const [rosterSlot, setRosterSlot] = useState()
  const currentDateInDayJs = dayjs(rosterSlot?.customDate)
  const [saving, setSaving] = useState(false)

  async function removeSlot() {
    setSaving(true)
    const resp = await putSPDataWithoutParams(`${disableSlotApiSignature}/${selectedSlot?.id}`)
    if (resp.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const rosterItem = getDataMapFromCommonResponse(resp)?.['clinic-roster-slot']
        removeSlotFromRosterList(rosterItem?.id)
        closePopup()
        return
      } else {
        toast.error(`${getMessageMapFromCommonResponse(resp)}`)
      }
    }
    setSaving(false)
  }

  const selectedMentor = useMemo(() => {
    return legendItems?.find(
      (itm) => getSidebarLegendValue(itm) === getSidebarItemReferenceFromRosterItem(rosterSlot),
    )
  }, [rosterSlot, legendItems, getSidebarLegendValue])

  useEffect(() => {
    setRosterSlot(selectedSlot)
  }, [selectedSlot])

  const mentorProfilePicId = selectedMentor?.profilePicId || null
  const mentorProfilePicUrl = mentorProfilePicId ? getImageLink(mentorProfilePicId) : null
  const mentorName = getSidebarLegendLabel(selectedMentor)

  return (
    <>
      <DialogContent sx={{ p: 3, '&.MuiDialogContent-root': { pt: 3 } }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <AccessTime sx={{ color: 'primary.main', fontSize: '1.5rem' }} />
            <Box>
              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                Date & Time
              </Box>
              <Box sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
                {currentDateInDayJs.format('dddd, D MMMM YYYY')}
              </Box>
              <Box sx={{ fontSize: '0.9rem', color: 'text.secondary', mt: 0.5 }}>
                {rosterSlot?.slotStartTime} - {rosterSlot?.slotEndTime}
              </Box>
            </Box>
          </Box>
          <Box>
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
              Assigned Practitioner
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                src={mentorProfilePicUrl}
                sx={{ 
                  width: 48, 
                  height: 48, 
                  fontSize: '1.25rem',
                  boxShadow: 2,
                  border: '2px solid',
                  borderColor: 'divider',
                }}
              >
                {!mentorProfilePicUrl && (sidebarIcon || <>{mentorName?.[0]?.toUpperCase()}</>)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {mentorName || 'Unassigned'}
                </Typography>
                {selectedMentor?.specializations && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                    {Array.isArray(selectedMentor.specializations) 
                      ? selectedMentor.specializations.join(', ')
                      : selectedMentor.specializations}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button 
          size="small" 
          onClick={closePopup}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
        >
          Close
        </Button>
        <Button 
          disabled={saving} 
          size="small" 
          onClick={removeSlot}
          variant="contained"
          color="error"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
            },
            '&:disabled': {
              opacity: 0.5,
            },
          }}
        >
          {saving ? 'Removing...' : 'Remove Slot'}
        </Button>
      </DialogActions>
    </>
  )
}
